import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, Typography, Card, CardContent, Button, Box,
  TextField, IconButton, Alert, CircularProgress, List,
  ListItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Tabs, Tab, Paper, Chip, Grid, Divider
} from '@mui/material';
import { Add, Delete, Edit, Save } from '@mui/icons-material';
import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE = API_BASE_URL;

const AdminFormCustomizationPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form options state - NEW STRUCTURE
  const [formOptions, setFormOptions] = useState({
    schools: {},
    courseLevels: [],
    honors: [],
    countries: []
  });

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'school', 'degree', 'emphasis', 'courseLevel', 'honor', 'country'
  const [dialogValue, setDialogValue] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedDegree, setSelectedDegree] = useState('');

  useEffect(() => {
    fetchFormOptions();
  }, []);

  const fetchFormOptions = async () => {
    setLoading(true);
    try {
      const token = Cookies.get('idToken');
      const response = await axios.get(`${API_BASE}/admin/form-options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormOptions(response.data);
    } catch (err) {
      console.error('Error fetching form options:', err);
      setError('Failed to load form options');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('idToken');
      await axios.post(`${API_BASE}/admin/form-options`, formOptions, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Form options saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save options');
    } finally {
      setSaving(false);
    }
  };

  // ========== SCHOOL MANAGEMENT ==========
  const handleAddSchool = () => {
    setDialogType('school');
    setDialogValue('');
    setEditingIndex(null);
    setOpenDialog(true);
  };

  const handleEditSchool = (school) => {
    setDialogType('school');
    setDialogValue(school);
    setEditingIndex(school);
    setOpenDialog(true);
  };

  const handleDeleteSchool = (school) => {
    if (!window.confirm(`Delete "${school}" and all its degrees/emphases?`)) return;
    const newSchools = { ...formOptions.schools };
    delete newSchools[school];
    setFormOptions({ ...formOptions, schools: newSchools });
  };

  // ========== DEGREE MANAGEMENT ==========
  const handleAddDegree = (school) => {
    setDialogType('degree');
    setSelectedSchool(school);
    setDialogValue('');
    setEditingIndex(null);
    setOpenDialog(true);
  };

  const handleEditDegree = (school, degree, index) => {
    setDialogType('degree');
    setSelectedSchool(school);
    setDialogValue(degree);
    setEditingIndex(index);
    setOpenDialog(true);
  };

  const handleDeleteDegree = (school, index) => {
    const newSchools = { ...formOptions.schools };
    const deletedDegree = newSchools[school].degrees[index];

    // Remove degree from degrees array
    newSchools[school].degrees = newSchools[school].degrees.filter((_, i) => i !== index);

    // Remove emphasis for this degree if exists
    if (newSchools[school].emphases && newSchools[school].emphases[deletedDegree]) {
      delete newSchools[school].emphases[deletedDegree];
    }

    setFormOptions({ ...formOptions, schools: newSchools });
  };

  // ========== EMPHASIS MANAGEMENT ==========
  const handleManageEmphases = (school, degree) => {
    setSelectedSchool(school);
    setSelectedDegree(degree);
    setDialogType('emphasis-manager');
    setOpenDialog(true);
  };

  const handleAddEmphasis = () => {
    setDialogType('emphasis');
    setDialogValue('');
    setEditingIndex(null);
    setOpenDialog(true);
  };

  const handleEditEmphasis = (index, value) => {
    setDialogType('emphasis');
    setDialogValue(value);
    setEditingIndex(index);
    setOpenDialog(true);
  };

  const handleDeleteEmphasis = (index) => {
    const newSchools = { ...formOptions.schools };
    const emphases = newSchools[selectedSchool].emphases[selectedDegree] || [];
    newSchools[selectedSchool].emphases[selectedDegree] = emphases.filter((_, i) => i !== index);
    setFormOptions({ ...formOptions, schools: newSchools });
  };

  // ========== GENERIC LIST MANAGEMENT ==========
  const handleAddToList = (type) => {
    setDialogType(type);
    setDialogValue('');
    setEditingIndex(null);
    setOpenDialog(true);
  };

  const handleEditInList = (type, value, index) => {
    setDialogType(type);
    setDialogValue(value);
    setEditingIndex(index);
    setOpenDialog(true);
  };

  const handleDeleteFromList = (type, index) => {
    const newList = formOptions[type].filter((_, i) => i !== index);
    setFormOptions({ ...formOptions, [type]: newList });
  };

  // ========== DIALOG SUBMIT ==========
  const handleDialogSubmit = () => {
    if (!dialogValue.trim() && dialogType !== 'emphasis-manager') {
      setError('Value cannot be empty');
      return;
    }

    if (dialogType === 'school') {
      const newSchools = { ...formOptions.schools };
      if (editingIndex && editingIndex !== dialogValue) {
        // Rename school
        newSchools[dialogValue] = newSchools[editingIndex];
        delete newSchools[editingIndex];
      } else if (!editingIndex) {
        // New school with empty degrees and emphases
        newSchools[dialogValue] = {
          degrees: [],
          emphases: {}
        };
      }
      setFormOptions({ ...formOptions, schools: newSchools });

    } else if (dialogType === 'degree') {
      const newSchools = { ...formOptions.schools };
      if (editingIndex !== null) {
        // Edit degree
        newSchools[selectedSchool].degrees[editingIndex] = dialogValue;
      } else {
        // Add degree
        newSchools[selectedSchool].degrees.push(dialogValue);
      }
      setFormOptions({ ...formOptions, schools: newSchools });

    } else if (dialogType === 'emphasis') {
      const newSchools = { ...formOptions.schools };
      if (!newSchools[selectedSchool].emphases) {
        newSchools[selectedSchool].emphases = {};
      }
      if (!newSchools[selectedSchool].emphases[selectedDegree]) {
        newSchools[selectedSchool].emphases[selectedDegree] = [];
      }

      if (editingIndex !== null) {
        // Edit emphasis
        newSchools[selectedSchool].emphases[selectedDegree][editingIndex] = dialogValue;
      } else {
        // Add emphasis
        newSchools[selectedSchool].emphases[selectedDegree].push(dialogValue);
      }
      setFormOptions({ ...formOptions, schools: newSchools });

    } else if (['courseLevels', 'honors', 'countries'].includes(dialogType)) {
      // Generic list items
      const newList = [...formOptions[dialogType]];
      if (editingIndex !== null) {
        newList[editingIndex] = dialogValue;
      } else {
        newList.push(dialogValue);
      }
      setFormOptions({ ...formOptions, [dialogType]: newList });
    }

    setOpenDialog(false);
    setDialogValue('');
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>Loading form options...</Typography>
      </Container>
    );
  }

  const tabs = ['🏫 Schools & Degrees', '📚 Course Levels', '🏆 Honors', '🌍 Countries'];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">⚙️ Form Options Manager</Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSaveAll}
          disabled={saving}
          sx={{ bgcolor: '#046A38', '&:hover': { bgcolor: '#035028' } }}
        >
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="fullWidth">
          {tabs.map((tab, i) => <Tab key={i} label={tab} />)}
        </Tabs>
      </Paper>

      {/* Tab 0: Schools & Degrees */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Schools, Degrees & Emphases</Typography>
              <Button variant="outlined" startIcon={<Add />} onClick={handleAddSchool}>
                Add School
              </Button>
            </Box>

            {Object.keys(formOptions.schools).length === 0 ? (
              <Alert severity="info">No schools configured. Click "Add School" to start.</Alert>
            ) : (
              Object.entries(formOptions.schools).map(([school, schoolData]) => (
                <Card key={school} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    {/* School Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                        {school}
                      </Typography>
                      <Box>
                        <IconButton size="small" onClick={() => handleEditSchool(school)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteSchool(school)}>
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Degrees Section */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Degrees ({schoolData.degrees?.length || 0}):
                        </Typography>
                        <Button size="small" startIcon={<Add />} onClick={() => handleAddDegree(school)}>
                          Add Degree
                        </Button>
                      </Box>

                      <Grid container spacing={1}>
                        {(schoolData.degrees || []).map((degree, index) => (
                          <Grid item xs={12} key={index}>
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              bgcolor: 'background.paper',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider'
                            }}>
                              <Chip label={degree} size="small" sx={{ flex: 1 }} />

                              {/* Emphasis count badge */}
                              {schoolData.emphases?.[degree]?.length > 0 && (
                                <Chip
                                  label={`${schoolData.emphases[degree].length} emphases`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              )}

                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleManageEmphases(school, degree)}
                              >
                                Manage Emphases
                              </Button>

                              <IconButton size="small" onClick={() => handleEditDegree(school, degree, index)}>
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteDegree(school, index)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 1: Course Levels */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Course Levels</Typography>
              <Button variant="outlined" startIcon={<Add />} onClick={() => handleAddToList('courseLevels')}>
                Add Course Level
              </Button>
            </Box>

            <List>
              {formOptions.courseLevels.map((level, index) => (
                <ListItem key={index}>
                  <Typography sx={{ flex: 1 }}>{level}</Typography>
                  <IconButton onClick={() => handleEditInList('courseLevels', level, index)}>
                    <Edit />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDeleteFromList('courseLevels', index)}>
                    <Delete />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Tab 2: Honors */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Honors Options</Typography>
              <Button variant="outlined" startIcon={<Add />} onClick={() => handleAddToList('honors')}>
                Add Honor
              </Button>
            </Box>

            <List>
              {formOptions.honors.map((honor, index) => (
                <ListItem key={index}>
                  <Typography sx={{ flex: 1 }}>{honor || '(None)'}</Typography>
                  <IconButton onClick={() => handleEditInList('honors', honor, index)}>
                    <Edit />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDeleteFromList('honors', index)}>
                    <Delete />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Tab 3: Countries */}
      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Countries List</Typography>
              <Button variant="outlined" startIcon={<Add />} onClick={() => handleAddToList('countries')}>
                Add Country
              </Button>
            </Box>

            <List sx={{ maxHeight: 500, overflow: 'auto' }}>
              {formOptions.countries.map((country, index) => (
                <ListItem key={index}>
                  <Typography sx={{ flex: 1 }}>{country}</Typography>
                  <IconButton onClick={() => handleEditInList('countries', country, index)}>
                    <Edit />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDeleteFromList('countries', index)}>
                    <Delete />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog && dialogType !== 'emphasis-manager'}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingIndex !== null ? 'Edit' : 'Add'} {
            dialogType === 'school' ? 'School' :
            dialogType === 'degree' ? 'Degree' :
            dialogType === 'emphasis' ? 'Emphasis' :
            dialogType
          }
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={
              dialogType === 'school' ? 'School Name' :
              dialogType === 'degree' ? 'Degree Name' :
              dialogType === 'emphasis' ? 'Emphasis/Concentration' :
              'Value'
            }
            value={dialogValue}
            onChange={(e) => setDialogValue(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleDialogSubmit} variant="contained">
            {editingIndex !== null ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Emphasis Manager Dialog */}
      <Dialog
        open={openDialog && dialogType === 'emphasis-manager'}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Manage Emphases for {selectedDegree}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Emphases appear as optional dropdown when this degree is selected. Always include "Other" option.
            </Alert>

            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddEmphasis}
              sx={{ mb: 2 }}
            >
              Add Emphasis
            </Button>

            <List>
              {(formOptions.schools[selectedSchool]?.emphases?.[selectedDegree] || []).map((emphasis, index) => (
                <ListItem key={index}>
                  <Chip
                    label={emphasis}
                    sx={{ flex: 1 }}
                    color={emphasis === 'Other' ? 'warning' : 'default'}
                  />
                  <IconButton onClick={() => handleEditEmphasis(index, emphasis)}>
                    <Edit />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDeleteEmphasis(index)}>
                    <Delete />
                  </IconButton>
                </ListItem>
              ))}
              {(formOptions.schools[selectedSchool]?.emphases?.[selectedDegree]?.length === 0) && (
                <Alert severity="warning">
                  No emphases configured. This degree will not show an emphasis dropdown.
                </Alert>
              )}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminFormCustomizationPage;